import React, { useState } from "react";
import { brands, destinations, offers, rewardBenefits, testimonials } from "../mock";
import { ArrowRight, Star, Award, Gift, Percent, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

export const BrandsSection = () => (
  <section className="py-20 md:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
        <div className="max-w-2xl">
          <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Our Brands</span>
          <h2 className="text-3xl md:text-5xl font-serif font-medium text-neutral-900 mt-3 leading-tight">
            Nine distinctive brands, one rewarding stay
          </h2>
        </div>
        <a href="#" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-900 border-b border-neutral-900 pb-1 hover:text-[#c8102e] hover:border-[#c8102e] transition-colors">
          Explore all brands <ArrowRight size={14} />
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {brands.map((brand) => (
          <div key={brand.id} className="group relative overflow-hidden cursor-pointer">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={brand.image}
                alt={brand.name}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <h3 className="text-2xl font-serif font-medium mb-1">{brand.name}</h3>
              <p className="text-sm text-white/85 mb-3">{brand.tagline}</p>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-wide border-b border-white/50 pb-0.5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                Discover the brand <ChevronRight size={12} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const DestinationsSection = () => (
  <section className="py-20 md:py-28 bg-neutral-50">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
        <div className="max-w-2xl">
          <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Destinations</span>
          <h2 className="text-3xl md:text-5xl font-serif font-medium text-neutral-900 mt-3 leading-tight">
            Where would you like to stay?
          </h2>
        </div>
        <a href="#" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-900 border-b border-neutral-900 pb-1 hover:text-[#c8102e] hover:border-[#c8102e] transition-colors">
          Browse all destinations <ArrowRight size={14} />
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
        {destinations.map((d) => (
          <a key={d.id} href="#" className="group relative overflow-hidden block">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={d.image} alt={d.city} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white flex items-end justify-between">
              <div>
                <h3 className="text-xl font-serif font-medium">{d.city}</h3>
                <p className="text-xs text-white/80 uppercase tracking-wider mt-0.5">{d.country}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-medium">{d.properties}</div>
                <div className="text-[10px] text-white/80 uppercase tracking-wider">Hotels</div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

export const OffersSection = () => {
  const [active, setActive] = useState(String(offers[0].id));
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-10 max-w-2xl">
          <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Special Offers</span>
          <h2 className="text-3xl md:text-5xl font-serif font-medium text-neutral-900 mt-3 leading-tight">
            Handpicked offers, unforgettable stays
          </h2>
        </div>

        <Tabs value={active} onValueChange={setActive} className="w-full">
          <TabsList className="bg-transparent border-b border-neutral-200 w-full justify-start rounded-none h-auto p-0 mb-8 gap-8 flex-wrap">
            {offers.map((o) => (
              <TabsTrigger
                key={o.id}
                value={String(o.id)}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#c8102e] data-[state=active]:text-[#c8102e] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm font-medium text-neutral-600 hover:text-neutral-900"
              >
                {o.tag}
              </TabsTrigger>
            ))}
          </TabsList>

          {offers.map((o) => (
            <TabsContent key={o.id} value={String(o.id)} className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className="overflow-hidden aspect-[5/4]">
                  <img src={o.image} alt={o.title} className="w-full h-full object-cover" />
                </div>
                <div>
                  <span className="inline-block text-xs uppercase tracking-widest text-neutral-500 mb-3">{o.tag}</span>
                  <h3 className="text-3xl md:text-4xl font-serif font-medium text-neutral-900 mb-5 leading-tight">{o.title}</h3>
                  <p className="text-neutral-700 leading-relaxed mb-6 text-base">{o.description}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-widest mb-8">{o.validity}</p>
                  <div className="flex flex-wrap gap-3">
                    <Button className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-6 py-5 text-sm tracking-wide">Book this offer</Button>
                    <Button variant="outline" className="rounded-none border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white px-6 py-5 text-sm tracking-wide">View terms</Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export const RewardsSection = () => {
  const icons = [Sparkles, Award, Gift, Percent];
  return (
    <section className="py-20 md:py-28 bg-neutral-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#c8102e] blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#c8102e]/60 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <span className="text-[11px] tracking-[0.25em] font-medium text-[#ff6b7a] uppercase">AIP Rewards</span>
            <h2 className="text-3xl md:text-5xl font-serif font-medium mt-3 leading-tight">
              Earn 10 AIP Credits on every booking
            </h2>
            <p className="text-white/80 mt-5 text-base leading-relaxed max-w-lg">
              Every unique booking ID adds 10 AIP Credits to your wallet — instantly. Redeem credits against future stays, spa upgrades and complimentary breakfast at any AIP Booking property worldwide.
            </p>

            <div className="mt-6 inline-flex items-center gap-3 border border-white/15 bg-white/[0.05] px-5 py-4">
              <div className="w-12 h-12 rounded-full bg-[#c8102e] flex items-center justify-center">
                <Sparkles size={22} className="text-white" />
              </div>
              <div>
                <div className="text-2xl font-serif font-medium leading-none">+10 AIP Credits</div>
                <div className="text-xs text-white/70 uppercase tracking-widest mt-1">per unique booking ID</div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-6 py-5">Join for free</Button>
              <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-neutral-900 rounded-none px-6 py-5">Learn more</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rewardBenefits.map((tier, i) => {
              const Icon = icons[i];
              return (
                <div key={tier.tier} className="bg-white/[0.04] border border-white/10 p-6 hover:border-[#c8102e] transition-colors">
                  <Icon size={24} className="text-[#ff6b7a] mb-4" />
                  <h3 className="text-xl font-serif font-medium mb-1">{tier.tier}</h3>
                  <p className="text-xs text-white/60 uppercase tracking-widest mb-4">{tier.nights}</p>
                  <ul className="space-y-2">
                    {tier.perks.map((p) => (
                      <li key={p} className="text-sm text-white/85 flex items-start gap-2">
                        <span className="text-[#ff6b7a] mt-1.5 w-1 h-1 bg-[#ff6b7a] rounded-full flex-shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export const TestimonialsSection = () => (
  <section className="py-20 md:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-14 max-w-2xl mx-auto">
        <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Guest Stories</span>
        <h2 className="text-3xl md:text-5xl font-serif font-medium text-neutral-900 mt-3 leading-tight">
          Loved by travelers worldwide
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.map((t) => (
          <div key={t.id} className="border border-neutral-200 p-8 hover:border-[#c8102e] transition-colors">
            <div className="flex gap-1 mb-4">
              {Array.from({ length: t.rating }).map((_, i) => (
                <Star key={i} size={16} className="fill-[#c8102e] text-[#c8102e]" />
              ))}
            </div>
            <p className="text-neutral-800 leading-relaxed mb-6 font-serif text-lg">"{t.quote}"</p>
            <div className="border-t border-neutral-200 pt-4">
              <div className="font-medium text-neutral-900">{t.name}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mt-0.5">{t.location}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
